import { useEffect, useRef, useState, memo } from "react";
import { Box, Checkbox } from "@mui/material";

interface SyncedVideoProps {
  src: string;
  currentTime: number;
  multiVid?: boolean;
  onLoaded: (duration: number) => void;
  onFrameClick?: (videoUrl: string, time: number) => void;
  onPlayPauseChange?: (playing: boolean) => void;
  onTimeUpdate?: () => void;
  onEnded?: () => void;
  storeRef?: (ref: HTMLVideoElement | null) => void;
  style?: React.CSSProperties;
}

const SyncedVideo: React.FC<SyncedVideoProps> = ({
  src,
  currentTime,
  multiVid = true,
  onLoaded,
  onFrameClick,
  onTimeUpdate,
  onEnded,
  storeRef,
  style,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isActive, setIsActive] = useState(true);

  // Share videoRef with parent
  useEffect(() => {
    storeRef?.(videoRef.current);
  }, [videoRef.current]);


  // Sync play/pause when parent state changes
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    // HIGHEST PRIORITY: If the video is not active, ensure it is paused and do nothing else.
    if (!isActive) {
      videoElement.pause();
      return; // This early exit prevents the race condition.
    }

    // If the video IS active, its state should match the parent's `isPlaying` prop.
    // if (isPlaying) {
    //   const playPromise = videoElement.play();
    //   if (playPromise !== undefined) {
    //     playPromise.catch(error => {
    //       // Ignore the specific error that happens when a play() request is interrupted by a pause() call.
    //       if (error.name !== 'AbortError') {
    //         console.error("Video play failed:", error);
    //       }
    //     });
    //   }
    // } else {
      videoElement.pause();
    // }
  }, [isActive]);

  // Handle local checkbox toggle
  const toggleActive = () => {
    const v = videoRef.current;
    const newState = !isActive;
    setIsActive(newState);

    if (!v) return;

    if (newState) {
      // ✅ Became active — seek and optionally play
      v.currentTime = currentTime;
      // if (isPlaying) v.play();
    } else {
      // 🚫 Became inactive — pause and reset
      v.pause();
      v.currentTime = 0;
    }
  };

  if (!multiVid) return (
    <video
      ref={videoRef}
      src={src}
      muted
      crossOrigin="anonymous"
      preload="true"
      playsInline
      onLoadedMetadata={() => {
        const v = videoRef.current;
        if (v) {
          v.muted = true;   // ✅ force
          v.volume = 0;     // ✅ force
          onLoaded(v.duration);
        }
      }}
      onClick={() => {
        if (onFrameClick && videoRef.current) {
          onFrameClick(src, videoRef.current.currentTime);
        }
      }}
      // onTimeUpdate={() => {
      //   if (isPlaying) {
      //     onTimeUpdate?.()
      //   }
      // }}
      onEnded={() => {
        const v = videoRef.current;
        if (v) {
          v.pause();
          v.currentTime = 0;
        }
        onEnded?.();
      }}
      controls={false}
      style={{
        width: "100%",
        height: "auto",
        maxHeight: "65vh",
        objectFit: "contain",
        borderRadius: 8,
        backgroundColor: "#000",
        opacity: isActive ? 1 : 0.5,
        transition: "opacity 0.3s",
        ...style,
      }}
    />
  )

  return (
    <Box
      sx={{
        position: "relative",
        width: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Checkbox
        checked={isActive}
        onChange={toggleActive}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 3,
          color: "#fff",
          borderRadius: "6px",
          padding: "4px",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          boxShadow: "0 0 6px rgba(0,0,0,0.5)",
          transition: "all 0.2s ease",
          '&:hover': {
            backgroundColor: isActive
              ? "rgba(25, 118, 210, 1)"
              : "rgba(255, 255, 255, 0.3)",
            transform: "scale(1.1)",
          },
          '& .MuiSvgIcon-root': {
            fontSize: 28,
            color: isActive ? "#fff" : "#ccc",
          },
        }}
      />

      <video
        ref={videoRef}
        src={src}
        muted
        crossOrigin="anonymous"
        preload="true"
        playsInline
        onLoadedMetadata={() => {
          const v = videoRef.current;
          if (v) {
            v.muted = true;   // ✅ force
            v.volume = 0;     // ✅ force
            onLoaded(v.duration);
          }
        }}
        onClick={() => {
          if (onFrameClick && videoRef.current) {
            onFrameClick(src, videoRef.current.currentTime);
          }
        }}
        // onTimeUpdate={() => {
        //   console.log("time updated ", Date.now())
        //   if (isPlaying) {
        //     onTimeUpdate?.()
        //   }
        // }}
        onEnded={() => {
          const v = videoRef.current;
          if (v) {
            v.pause();
            v.currentTime = 0;
          }
          onEnded?.();
        }}
        controls={false}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: "65vh",
          objectFit: "contain",
          borderRadius: 8,
          backgroundColor: "#000",
          opacity: isActive ? 1 : 0.5,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
};

export default memo(SyncedVideo);